import apiClient from "@/api/apiClient";
import type { NotifyCaseAssigneeProps, NotifyCaseCreationProps } from "@/api/resources/cases/types";
import { CaseStatus, type ArgsExportCaseForCsv } from "@/supabase.types";
import {
  createPaginatedSupabaseQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";
import { type QueryPaginationFilters } from "@/utils/pagination";

const table = "cases";
const query = `
              *,
              public_id,
              device:devices(*, device_tags(*), connections_to_devices(*),manufacturer:manufacturers(*), model:device_models(*)),
              parent_maintenance:maintenances!cases_parent_maintenance_id_fkey(*),
              child_maintenance:maintenances!cases_child_maintenance_id_fkey(*),
              parts:cases_to_parts(id),
              case_tags(*),
              search_d:devices(name),
              search_pm:maintenances!cases_parent_maintenance_id_fkey(title),
              search_cm:maintenances!cases_child_maintenance_id_fkey(title),
              search_ct:case_tags(name)
              `;

export const apiConfigCases: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "public_id", "search_d.name", "search_pm.title", "search_cm.title", "search_ct.name"],
  sortables: ["name", "updated_at", "created_at", "resolved_at", "public_id"],
};

export const getCase = spawnGetApiFunction({ table, query });
export const createCase = spawnCreateApiFunction({ table, query });
export const updateCase = spawnUpdateApiFunction({ table, query });
export const deleteCase = spawnDeleteApiFunction({ table });

export const getChildCasesByMaintenance = async (maintenance_id: string) => {
  const { data, error } = await apiClient.from(table).select(query).eq("parent_maintenance_id", maintenance_id);
  if (error) throw error;
  return data;
};

export const getParentCaseByMaintenance = async (maintenance_id: string) => {
  const { data, error } = await apiClient
    .from(table)
    .select(query)
    .eq("child_maintenance_id", maintenance_id)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export async function getPaginatedCases({ dataTableOptions, customFilters, customOrFilters }: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigCases.searchables,
      sortables: apiConfigCases.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedCasesOpen({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigCases.searchables,
      sortables: apiConfigCases.sortables,
      filters: [["status", "eq", CaseStatus.open]],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedCasesResolved({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigCases.searchables,
      sortables: apiConfigCases.sortables,
      filters: [["status", "eq", CaseStatus.resolved]],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedCasesArchived({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigCases.searchables,
      sortables: apiConfigCases.sortables,
      filters: [["status", "eq", CaseStatus.archived]],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export const notifyCaseAssignee = async ({
  caseId,
  previousUserId,
  whitelabelBrandKey,
  appBaseUrl,
}: NotifyCaseAssigneeProps) => {
  const response = await apiClient.functions.invoke("notify-case-assignee", {
    body: {
      whitelabelBrandKey,
      appBaseUrl,
      caseId,
      previousUserId,
    },
  });
  if (response.error) throw response.error;
};

export const notifyCaseCreation = async ({
  caseId,
  email,
  locale,
  whitelabelBrandKey,
  appBaseUrl,
}: NotifyCaseCreationProps) => {
  const response = await apiClient.functions.invoke("notify-case-creation", {
    body: {
      whitelabelBrandKey,
      appBaseUrl,
      caseId,
      email,
      locale,
    },
  });
  if (response.error) throw response.error;
};

export const exportCasesCsv = async (args: ArgsExportCaseForCsv) => {
  const { error, ...rest } = await apiClient.rpc("export_cases_for_csv", args).csv();
  if (error) throw error;

  return { error, ...rest };
};
